import { lazy, Suspense } from 'react';
import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { Permission } from '@kreart/shared';
import { authStore } from '@/features/auth/store';
import { bootstrapAuth } from '@/features/auth/bootstrap';
import { AppLayout } from './AppLayout';
import { LoginPage } from '@/features/auth/components/LoginPage';
import { ChangePasswordPage } from '@/features/auth/components/ChangePasswordPage';
import { HomePage } from '@/features/home/components/HomePage';
import { parseUsersListSearch, type UsersListSearch } from '@/features/users/search';
import { ClientsListPage } from '@/features/clients/components/ClientsListPage';
import { ClientCreatePage } from '@/features/clients/components/ClientCreatePage';
import { parseClientsListSearch, type ClientsListSearch } from '@/features/clients/search';
import { ProjectsListPage } from '@/features/projects/components/ProjectsListPage';
import { ProjectCreatePage } from '@/features/projects/components/ProjectCreatePage';
import { parseProjectsListSearch, type ProjectsListSearch } from '@/features/projects/search';
import { LIBRARY_FEATURE_ENABLED } from '@/features/library/featureFlags';
import { OPERATIONS_PLANNER_FEATURE_ENABLED } from '@/features/project-planning/featureFlags';
import { parseTimesheetSearch } from '@/features/operations-scheduler/timesheet-tabs';

// Lazy-loaded routes — heavy or less-frequented pages get their own chunks so
// the hot path (login → dashboard → projects/clients lists) stays small.
// recharts (~400 KB raw) only ships with the analytics chunk.
const UsersListPage = lazy(() =>
  import('@/features/users/components/UsersListPage').then((m) => ({ default: m.UsersListPage })),
);
const UserCreatePage = lazy(() =>
  import('@/features/users/components/UserCreatePage').then((m) => ({ default: m.UserCreatePage })),
);
const UserEditPage = lazy(() =>
  import('@/features/users/components/UserEditPage').then((m) => ({ default: m.UserEditPage })),
);
const ClientEditPage = lazy(() =>
  import('@/features/clients/components/ClientEditPage').then((m) => ({
    default: m.ClientEditPage,
  })),
);
const ProjectDetailsPage = lazy(() =>
  import('@/features/projects/components/ProjectDetailsPage').then((m) => ({
    default: m.ProjectDetailsPage,
  })),
);
const AnalyticsPage = lazy(() =>
  import('@/features/analytics/components/AnalyticsPage').then((m) => ({
    default: m.AnalyticsPage,
  })),
);
const AuditLogsPage = lazy(() =>
  import('@/features/audit/components/AuditLogsPage').then((m) => ({ default: m.AuditLogsPage })),
);
const OperationsPlannerPage = lazy(() =>
  import('@/features/project-planning/components/OperationsPlannerPage').then((m) => ({
    default: m.OperationsPlannerPage,
  })),
);
const TimesheetPage = lazy(() =>
  import('@/features/operations-scheduler/components/TimesheetPage').then((m) => ({
    default: m.TimesheetPage,
  })),
);
const OperationsSchedulerPage = lazy(() =>
  import('@/features/operations-scheduler/components/OperationsSchedulerPage').then((m) => ({
    default: m.OperationsSchedulerPage,
  })),
);
const ProjectOrganizationPage = lazy(() =>
  import('@/features/project-organization/components/ProjectOrganizationPage').then((m) => ({
    default: m.ProjectOrganizationPage,
  })),
);
const LibraryPage = lazy(() =>
  import('@/features/library/components/LibraryPage').then((m) => ({ default: m.LibraryPage })),
);

function RouteFallback() {
  return (
    <div
      className="flex min-h-[200px] items-center justify-center text-sm text-muted-foreground"
      role="status"
      aria-live="polite"
    >
      <span className="sr-only">Loading…</span>
    </div>
  );
}
import {
  CabinetsListPage,
  CabinetCreatePage,
  CabinetEditPage,
} from '@/features/catalog/components/CabinetsPages';
import {
  PanelsListPage,
  PanelCreatePage,
  PanelEditPage,
} from '@/features/catalog/components/PanelsPages';
import {
  CountertopsListPage,
  CountertopCreatePage,
  CountertopEditPage,
} from '@/features/catalog/components/CountertopsPages';
import {
  DrawersListPage,
  DrawerCreatePage,
  DrawerEditPage,
} from '@/features/catalog/components/DrawersPages';
import {
  ElementsListPage,
  ElementCreatePage,
  ElementEditPage,
} from '@/features/catalog/components/ElementsPages';
import {
  LightingListPage,
  LightingCreatePage,
  LightingEditPage,
} from '@/features/catalog/components/LightingPages';
import {
  HardwareListPage,
  HardwareCreatePage,
  HardwareEditPage,
} from '@/features/catalog/components/HardwarePages';
import { parseCatalogListSearch, type CatalogListSearch } from '@/features/catalog/search';

const MaterialsAdminPage = lazy(() =>
  import('@/features/catalog/components/admin/MaterialsAdminPage').then((m) => ({
    default: m.MaterialsAdminPage,
  })),
);
const EdgesAdminPage = lazy(() =>
  import('@/features/catalog/components/admin/EdgesAdminPage').then((m) => ({
    default: m.EdgesAdminPage,
  })),
);
const DrawerSystemsAdminPage = lazy(() =>
  import('@/features/catalog/components/admin/DrawerSystemsAdminPage').then((m) => ({
    default: m.DrawerSystemsAdminPage,
  })),
);
const rootRoute = createRootRoute({
  component: () => <Outlet />,
  beforeLoad: async () => {
    if (authStore.snapshot.status === 'unknown') {
      await bootstrapAuth();
    }
  },
});

type LoginSearch = { redirect?: string; changed?: string };

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
  validateSearch: (raw: Record<string, unknown>): LoginSearch => {
    const out: LoginSearch = {};
    if (typeof raw['redirect'] === 'string') out.redirect = raw['redirect'];
    if (raw['changed'] === '1') out.changed = '1';
    return out;
  },
  beforeLoad: () => {
    const snap = authStore.snapshot;
    if (snap.status === 'authenticated') {
      if (snap.user?.mustChangePassword) {
        throw redirect({ to: '/change-password' });
      }
      throw redirect({ to: '/' });
    }
  },
});

const protectedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'protected',
  component: () => (
    <AppLayout>
      <Suspense fallback={<RouteFallback />}>
        <Outlet />
      </Suspense>
    </AppLayout>
  ),
  beforeLoad: ({ location }) => {
    const snap = authStore.snapshot;
    if (snap.status !== 'authenticated') {
      throw redirect({ to: '/login', search: { redirect: location.href } });
    }
    // Enforce forced password change on ALL protected routes except /change-password itself.
    if (snap.user?.mustChangePassword && location.pathname !== '/change-password') {
      throw redirect({ to: '/change-password' });
    }
  },
});

const homeRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/',
  component: HomePage,
});

const changePasswordRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/change-password',
  component: ChangePasswordPage,
});

function requirePermission(permission: string) {
  return () => {
    const snap = authStore.snapshot;
    if (!snap.user?.permissions.includes(permission)) {
      throw redirect({ to: '/' });
    }
  };
}

function requireAllPermissions(...permissions: string[]) {
  return () => {
    const perms = authStore.snapshot.user?.permissions ?? [];
    if (!permissions.every((p) => perms.includes(p))) {
      throw redirect({ to: '/' });
    }
  };
}

const usersListRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/users',
  component: UsersListPage,
  validateSearch: (raw: Record<string, unknown>): UsersListSearch => {
    const isAdmin = authStore.snapshot.user?.role === 'ADMIN';
    return parseUsersListSearch(raw, { isAdmin });
  },
  beforeLoad: requirePermission(Permission.USERS_LIST),
});

const userCreateRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/users/new',
  component: UserCreatePage,
  beforeLoad: requirePermission(Permission.USERS_CREATE),
});

const userEditRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/users/$id',
  component: UserEditPage,
  beforeLoad: requirePermission(Permission.USERS_READ),
});

const clientsListRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/clients',
  component: ClientsListPage,
  validateSearch: (raw: Record<string, unknown>): ClientsListSearch => {
    const isAdmin = authStore.snapshot.user?.role === 'ADMIN';
    return parseClientsListSearch(raw, { isAdmin });
  },
  beforeLoad: requirePermission(Permission.CLIENTS_LIST),
});

const clientCreateRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/clients/new',
  component: ClientCreatePage,
  beforeLoad: requirePermission(Permission.CLIENTS_CREATE),
});

const clientEditRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/clients/$id',
  component: ClientEditPage,
  beforeLoad: requirePermission(Permission.CLIENTS_READ),
});

const projectsListRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/projects',
  component: ProjectsListPage,
  validateSearch: (raw: Record<string, unknown>): ProjectsListSearch => {
    const isAdmin = authStore.snapshot.user?.role === 'ADMIN';
    return parseProjectsListSearch(raw, { isAdmin });
  },
  beforeLoad: requirePermission(Permission.PROJECTS_LIST),
});

const projectCreateRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/projects/new',
  component: ProjectCreatePage,
  beforeLoad: requirePermission(Permission.PROJECTS_CREATE),
});

const projectDetailsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/projects/$id',
  component: ProjectDetailsPage,
  validateSearch: (raw: Record<string, unknown>): { room?: string; view?: 'master-bom' } => {
    const r = raw.room;
    if (typeof r === 'string' && r.length > 0) return { room: r };
    // Master BOM is a view of "All Rooms", so it means nothing next to a room.
    return raw.view === 'master-bom' ? { view: 'master-bom' } : {};
  },
  beforeLoad: requirePermission(Permission.PROJECTS_READ),
});

const operationsPlannerRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/operations-planner',
  component: OperationsPlannerPage,
  beforeLoad: () => {
    // Feature-gated ahead of the permission check, so a bookmarked or typed URL
    // lands home rather than on a page that is meant to be unreachable.
    if (!OPERATIONS_PLANNER_FEATURE_ENABLED) {
      throw redirect({ to: '/' });
    }
    requirePermission(Permission.PROJECT_PLANNING_READ)();
  },
});

const timesheetRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/timesheet',
  component: TimesheetPage,
  // `tab` opens one of the page's panes (employees, teams, …) — the board's
  // "Echipe și angajați" and its unstaffed-team notification link straight in.
  validateSearch: parseTimesheetSearch,
  beforeLoad: requirePermission(Permission.PROJECT_PLANNING_READ),
});

const operationsSchedulerRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/operations-scheduler',
  component: OperationsSchedulerPage,
  beforeLoad: requirePermission(Permission.PROJECT_PLANNING_READ),
});

const projectOrganizationRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/project-organization',
  component: ProjectOrganizationPage,
  // `projectId` deep-links straight to one project's costing, which is what
  // the button on the project page uses. Absent, the page opens unselected.
  validateSearch: (raw: Record<string, unknown>): { projectId?: string } => {
    const id = raw.projectId;
    return typeof id === 'string' && id.length > 0 ? { projectId: id } : {};
  },
  // The page is built around offer totals and BOM costs, so it requires the
  // financial-read permission rather than the planning one.
  beforeLoad: requirePermission(Permission.PROJECTS_READ_FINANCIALS),
});

const cabinetsListRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/cabinets',
  component: CabinetsListPage,
  validateSearch: (raw: Record<string, unknown>): CatalogListSearch => {
    const isAdmin = authStore.snapshot.user?.role === 'ADMIN';
    return parseCatalogListSearch(raw, { isAdmin });
  },
  beforeLoad: requirePermission(Permission.CATALOG_READ),
});

const cabinetCreateRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/cabinets/new',
  component: CabinetCreatePage,
  beforeLoad: requirePermission(Permission.CATALOG_CREATE),
});

const cabinetEditRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/cabinets/$id',
  component: CabinetEditPage,
  beforeLoad: requirePermission(Permission.CATALOG_READ),
});

const panelsListRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/panels',
  component: PanelsListPage,
  validateSearch: (raw: Record<string, unknown>): CatalogListSearch => {
    const isAdmin = authStore.snapshot.user?.role === 'ADMIN';
    return parseCatalogListSearch(raw, { isAdmin });
  },
  beforeLoad: requirePermission(Permission.CATALOG_READ),
});

const panelCreateRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/panels/new',
  component: PanelCreatePage,
  beforeLoad: requirePermission(Permission.CATALOG_CREATE),
});

const panelEditRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/panels/$id',
  component: PanelEditPage,
  beforeLoad: requirePermission(Permission.CATALOG_READ),
});

const countertopsListRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/countertops',
  component: CountertopsListPage,
  validateSearch: (raw: Record<string, unknown>): CatalogListSearch => {
    const isAdmin = authStore.snapshot.user?.role === 'ADMIN';
    return parseCatalogListSearch(raw, { isAdmin });
  },
  beforeLoad: requirePermission(Permission.CATALOG_READ),
});

const countertopCreateRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/countertops/new',
  component: CountertopCreatePage,
  beforeLoad: requirePermission(Permission.CATALOG_CREATE),
});

const countertopEditRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/countertops/$id',
  component: CountertopEditPage,
  beforeLoad: requirePermission(Permission.CATALOG_READ),
});

const drawersListRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/drawers',
  component: DrawersListPage,
  validateSearch: (raw: Record<string, unknown>): CatalogListSearch => {
    const isAdmin = authStore.snapshot.user?.role === 'ADMIN';
    return parseCatalogListSearch(raw, { isAdmin });
  },
  beforeLoad: requirePermission(Permission.CATALOG_READ),
});

const drawerCreateRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/drawers/new',
  component: DrawerCreatePage,
  beforeLoad: requirePermission(Permission.CATALOG_CREATE),
});

const drawerEditRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/drawers/$id',
  component: DrawerEditPage,
  beforeLoad: requirePermission(Permission.CATALOG_READ),
});

const elementsListRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/elements',
  component: ElementsListPage,
  validateSearch: (raw: Record<string, unknown>): CatalogListSearch => {
    const isAdmin = authStore.snapshot.user?.role === 'ADMIN';
    return parseCatalogListSearch(raw, { isAdmin });
  },
  beforeLoad: requirePermission(Permission.CATALOG_READ),
});

const elementCreateRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/elements/new',
  component: ElementCreatePage,
  beforeLoad: requirePermission(Permission.CATALOG_CREATE),
});

const elementEditRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/elements/$id',
  component: ElementEditPage,
  beforeLoad: requirePermission(Permission.CATALOG_READ),
});

const lightingListRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/lighting',
  component: LightingListPage,
  validateSearch: (raw: Record<string, unknown>): CatalogListSearch => {
    const isAdmin = authStore.snapshot.user?.role === 'ADMIN';
    return parseCatalogListSearch(raw, { isAdmin });
  },
  beforeLoad: requirePermission(Permission.CATALOG_COMPONENTS),
});

const lightingCreateRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/lighting/new',
  component: LightingCreatePage,
  beforeLoad: requireAllPermissions(Permission.CATALOG_COMPONENTS, Permission.CATALOG_CREATE),
});

const lightingEditRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/lighting/$id',
  component: LightingEditPage,
  beforeLoad: requirePermission(Permission.CATALOG_COMPONENTS),
});

const hardwareListRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/hardware',
  component: HardwareListPage,
  validateSearch: (raw: Record<string, unknown>): CatalogListSearch => {
    const isAdmin = authStore.snapshot.user?.role === 'ADMIN';
    return parseCatalogListSearch(raw, { isAdmin });
  },
  beforeLoad: requirePermission(Permission.CATALOG_COMPONENTS),
});

const hardwareCreateRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/hardware/new',
  component: HardwareCreatePage,
  beforeLoad: requireAllPermissions(Permission.CATALOG_COMPONENTS, Permission.CATALOG_CREATE),
});

const hardwareEditRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/hardware/$id',
  component: HardwareEditPage,
  beforeLoad: requirePermission(Permission.CATALOG_COMPONENTS),
});

const analyticsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/analytics',
  component: AnalyticsPage,
  validateSearch: (raw: Record<string, unknown>): { months?: 3 | 6 | 12 } => {
    const v = raw['months'];
    const num = typeof v === 'string' ? Number(v) : typeof v === 'number' ? v : undefined;
    if (num === 3 || num === 6 || num === 12) return { months: num };
    return {};
  },
});

const auditRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/audit',
  component: AuditLogsPage,
  beforeLoad: requirePermission(Permission.AUDIT_READ),
});

const libraryRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/library',
  component: LibraryPage,
  beforeLoad: () => {
    if (!LIBRARY_FEATURE_ENABLED) {
      throw redirect({ to: '/' });
    }
  },
});

const materialsAdminRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/admin/catalog/materials',
  component: MaterialsAdminPage,
  beforeLoad: requirePermission(Permission.CATALOG_COMPONENTS),
});

const edgesAdminRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/admin/catalog/edges',
  component: EdgesAdminPage,
  beforeLoad: requirePermission(Permission.CATALOG_COMPONENTS),
});

const drawerSystemsAdminRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/admin/catalog/drawer-systems',
  component: DrawerSystemsAdminPage,
  beforeLoad: requirePermission(Permission.CATALOG_COMPONENTS),
});

const hardwareAdminRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/admin/catalog/hardware',
  component: HardwareListPage,
  beforeLoad: requirePermission(Permission.CATALOG_COMPONENTS),
});

const lightingAdminRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/admin/catalog/lighting',
  component: LightingListPage,
  beforeLoad: requirePermission(Permission.CATALOG_COMPONENTS),
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  protectedRoute.addChildren([
    homeRoute,
    changePasswordRoute,
    usersListRoute,
    userCreateRoute,
    userEditRoute,
    clientsListRoute,
    clientCreateRoute,
    clientEditRoute,
    projectsListRoute,
    projectCreateRoute,
    projectDetailsRoute,
    operationsPlannerRoute,
    operationsSchedulerRoute,
    timesheetRoute,
    projectOrganizationRoute,
    cabinetsListRoute,
    cabinetCreateRoute,
    cabinetEditRoute,
    panelsListRoute,
    panelCreateRoute,
    panelEditRoute,
    countertopsListRoute,
    countertopCreateRoute,
    countertopEditRoute,
    drawersListRoute,
    drawerCreateRoute,
    drawerEditRoute,
    elementsListRoute,
    elementCreateRoute,
    elementEditRoute,
    lightingListRoute,
    lightingCreateRoute,
    lightingEditRoute,
    hardwareListRoute,
    hardwareCreateRoute,
    hardwareEditRoute,
    analyticsRoute,
    auditRoute,
    libraryRoute,
    materialsAdminRoute,
    edgesAdminRoute,
    drawerSystemsAdminRoute,
    hardwareAdminRoute,
    lightingAdminRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
